// @flow
import React, { Fragment } from "react";
import { Table } from "reactstrap";
import classNames from "classnames";
import { Icons } from "..";

export type ColConfig<T> = $Exact<{
  name: string, // $Keys<T> doesn't work because we have columns with actions, etc
  title?: string | React$Node,
  headingClassName?: string,
  itemClassName?: string,
  renderItem?: (line: T, activeRow: boolean) => ?string | React$Node,
  sortable?: boolean,
  align?: "left" | "center" | "right",
  width?: number,
  hidden?: boolean,
  titleAlign?: "left" | "center" | "right",
  nowrap?: boolean,
  minWidth?: number,
  tdStyle?: Object,
}>;

export type GridSortType = $Exact<{
  id: string | null,
  direction: "asc" | "desc",
}>;

type GridTableProps<T> = {
  className?: string,
  selected?: Array<number>,
  data?: Array<T>,
  disabled?: boolean,
  config: {
    trStyle?: Object,
    cols: Array<ColConfig<T>>,
    idIdentifier?: string,
    onLineClick?: Function,
    lineClassName?: string,
  },
  sort?: GridSortType,
  onSortChange?: (sortingParams: GridSortType) => void,
  noResultsText: string,
  onHover?: (rowIndex: number) => void,
  updateOnHover?: boolean,
  activeRowIndex?: number,
  style?: Object,
  tableTitle?: string,
};

type GridTableState = $Exact<{
  activeRowIndex: number,
}>;

export class GridTable<T: Object> extends React.Component<GridTableProps<T>, GridTableState> {
  static defaultProps = {
    noResultsText: "No results for the specified criteria",
  };

  state = {
    activeRowIndex: -1,
  };

  sortHandler = (event: SyntheticEvent<HTMLButtonElement>) => {
    const columnName = event.currentTarget.getAttribute("data-column-name") || null;
    if (this.props.onSortChange) {
      let direction = "asc";
      if (
        this.props.sort &&
        this.props.sort.id === columnName &&
        this.props.sort.direction === "asc"
      ) {
        direction = "desc";
      }
      this.props.onSortChange({
        id: columnName,
        direction,
      });
    }
  };

  handleRowEnter = (event: SyntheticEvent<HTMLElement>) => {
    if (!this.props.updateOnHover) {
      return;
    }

    const activeRowIndex = +event.currentTarget.getAttribute("data-index");
    this.setState({
      activeRowIndex,
    });

    if (this.props.onHover) {
      this.props.onHover(activeRowIndex);
    }
  };

  handleRowLeave = (event: SyntheticEvent<HTMLElement>) => {
    if (!this.props.updateOnHover) {
      return;
    }

    this.setState({
      activeRowIndex: -1,
    });

    if (this.props.onHover) {
      this.props.onHover(-1);
    }
  };

  render() {
    return (
      <Fragment>
        {this.props.data && this.props.data.length < 1 ? (
          <div
            className={classNames(
              "text-center text-secondary no-results-text w-50 mx-auto",
              this.props.className,
            )}
          >
            {this.props.noResultsText}
          </div>
        ) : (
          <Table
            className={classNames("grid-table", this.props.className)}
            style={this.props.style || {}}
          >
            <thead>
              {this.props.tableTitle ? (
                <tr>
                  <th
                    className="big-font-size text-orange text-center"
                    colSpan={this.props.config.cols.filter(col => !col.hidden).length}
                  >
                    {this.props.tableTitle}
                  </th>
                </tr>
              ) : (
                <tr>
                  {this.props.config.cols
                    .filter(col => !col.hidden)
                    .map(col => (
                      <th
                        className={classNames(
                          col.headingClassName,
                          `grid-table-${col.name}-header`,
                        )}
                        data-column-name={col.name}
                        key={col.name}
                        onClick={col.sortable ? this.sortHandler : null}
                        style={{
                          width: col.width ? `${col.width}px` : null,
                          minWidth: col.minWidth ? `${col.minWidth}px` : null,
                        }}
                      >
                        <div
                          className={classNames("d-flex", {
                            "justify-content-start":
                              !col.align || (col.align && col.align === "left"),
                            "justify-content-center": col.align && col.align === "center",
                            "justify-content-end": col.align && col.align === "right",
                            "text-left":
                              !col.titleAlign || (col.titleAlign && col.titleAlign === "left"),
                            "text-center": col.titleAlign && col.titleAlign === "center",
                            "text-right": col.titleAlign && col.titleAlign === "right",
                          })}
                        >
                          {this.props.sort && this.props.sort.id === col.name ? (
                            <div className="d-flex flex-column sortable">
                              <span>
                                <Icons.ArrowDownIcon
                                  color="black"
                                  rotate={this.props.sort.direction === "desc" ? 180 : 0}
                                />
                              </span>
                              <u className="text-color-black pt-2">{col.title || ""}</u>
                            </div>
                          ) : (
                            <span
                              className={classNames("", {
                                sortable: col.sortable,
                                "text-nowrap": col.nowrap,
                              })}
                            >
                              {col.title || ""}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                </tr>
              )}
            </thead>
            <tbody>
              {this.props.data &&
                this.props.data.map((row, i) => (
                  <tr
                    key={row[this.props.config.idIdentifier || "id"] || i}
                    style={this.props.config.trStyle || {}}
                    className={classNames(
                      {
                        selected:
                          this.props.selected &&
                          this.props.selected.includes(row[this.props.config.idIdentifier || "id"]),
                        active: this.props.activeRowIndex === i || this.state.activeRowIndex === i,
                        disabled: this.props.disabled,
                      },
                      this.props.config.lineClassName,
                      row.className || "",
                    )}
                    onClick={this.props.config.onLineClick}
                    onMouseEnter={this.handleRowEnter}
                    onMouseLeave={this.handleRowLeave}
                    data-id={row[this.props.config.idIdentifier || "id"]}
                    data-index={i}
                  >
                    {this.props.config.cols
                      .filter(col => !col.hidden)
                      .map(col => (
                        <td
                          style={col.tdStyle}
                          key={col.name}
                          className={classNames(`grid-table-${col.name}-item`, col.itemClassName)}
                        >
                          <div
                            className={classNames("d-flex", {
                              "justify-content-start":
                                !col.align || (col.align && col.align === "left"),
                              "justify-content-center": col.align && col.align === "center",
                              "justify-content-end": col.align && col.align === "right",
                              "text-left":
                                !col.titleAlign || (col.titleAlign && col.titleAlign === "left"),
                              "text-center": col.titleAlign && col.titleAlign === "center",
                              "text-right": col.titleAlign && col.titleAlign === "right",
                              "text-nowrap": col.nowrap,
                            })}
                          >
                            {!col.hidden && col.renderItem
                              ? col.renderItem(
                                  row,
                                  this.props.activeRowIndex === i ||
                                    this.state.activeRowIndex === i,
                                )
                              : row[col.name]}
                          </div>
                        </td>
                      ))}
                  </tr>
                ))}
            </tbody>
          </Table>
        )}
      </Fragment>
    );
  }
}

export default GridTable;
